import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { useEffect, useEffectEvent, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { GetPortalInvoiceResponse } from '@snootsvet/shared/portal'
import { formatCurrency } from '@snootsvet/shared/portal'

import {
  InvoiceStripeLink,
  PaymentProcessing,
} from '@/customer/components/billing/billing-primitives'
import { InvoiceCard } from '@/customer/components/billing/invoice-card'
import { PaymentReconciliationFailed } from '@/customer/components/billing/payment-reconciliation-failed'
import { StripePaymentWidget } from '@/customer/components/billing/stripe-payment-widget'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { NotFound } from '@/customer/components/not-found'
import { PageContainer } from '@/customer/components/page-container'
import type { StackedHeadlineItem } from '@/customer/components/stacked-headline'
import { Alert, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetInvoice } from '@/customer/hooks/use-get-invoice'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { petSubscriptionQueryKey } from '@/customer/hooks/use-get-pet-subscription'
import { invoicesQueryKey } from '@/customer/hooks/use-list-invoices'
import { useRegion } from '@/customer/hooks/use-region'
import { resolvePetNames } from '@/customer/lib/pets'
import { routes } from '@/customer/routes'

const BACK_LINK = { to: routes.account.billing.index, label: 'Billing' } as const

const RECONCILIATION_POLL_MS = 2000
const MAX_RECONCILIATION_ATTEMPTS = 5
const PAGE_DESCRIPTION = 'Review your invoice and payment details.'

const DEFAULT_HEADLINE: StackedHeadlineItem[] = [
  { text: 'Invoice', color: 'yellow' },
  { text: 'details', color: 'green' },
]

const PAY_HEADLINE: StackedHeadlineItem[] = [
  { text: 'Pay', color: 'yellow' },
  { text: 'invoice', color: 'orange' },
]

const UPGRADE_HEADLINE: StackedHeadlineItem[] = [
  { text: 'Complete', color: 'blue' },
  { text: 'upgrade', color: 'green' },
]

type Invoice = GetPortalInvoiceResponse['invoice']

/** Broader than `overdue` - covers any open invoice the member can act on, including pending upgrade charges that haven't failed yet. */
function invoiceNeedsPayment(invoice: Invoice | null | undefined): boolean {
  return !!invoice && invoice.status === 'open' && invoice.amountDue > 0
}

function headlineForInvoice(invoice: Invoice | null | undefined): StackedHeadlineItem[] {
  if (!invoiceNeedsPayment(invoice)) return DEFAULT_HEADLINE
  return invoice?.pendingSubscriptionUpdate ? UPGRADE_HEADLINE : PAY_HEADLINE
}

/**
 * Invoice detail page with integrated payment flow. Shows the invoice
 * card for all statuses; overdue invoices additionally get card
 * selection and payment confirmation. After Stripe confirms a charge,
 * polls the invoices query waiting for the backend to reconcile.
 */
export function InvoicePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const me = useGetMe()
  const { data, isPending: invoiceLoading, refetch } = useGetInvoice(invoiceId)
  const region = useRegion()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const invoice = data?.invoice ?? null

  const petNamesById = new Map(me.data?.patients.map((p) => [p.id, p.name]) ?? [])
  const petName = invoice ? resolvePetNames(invoice.petIds, petNamesById) : ''

  const headline = headlineForInvoice(invoice)
  const needsPayment = invoiceNeedsPayment(invoice)
  const isPendingUpgrade = invoice?.pendingSubscriptionUpdate ?? false
  useDocumentTitle(
    isPendingUpgrade ? 'Complete upgrade' : needsPayment ? 'Pay invoice' : 'Invoice details'
  )

  const [reconciliationAttempts, setReconciliationAttempts] = useState(0)

  const reportReconciliationTimeout = useEffectEvent(() => {
    Sentry.captureException(new Error('Invoice not settled after successful card confirmation'), {
      extra: { invoiceId, petIds: invoice?.petIds },
    })
  })

  useEffect(() => {
    if (reconciliationAttempts === 0) return

    if (invoice?.status === 'paid') {
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
      if (invoice.source === 'subscription') {
        for (const petId of invoice.petIds) {
          queryClient.invalidateQueries({ queryKey: petSubscriptionQueryKey(petId) })
        }
      }
      navigate(routes.account.billing.index)
      return
    }

    if (reconciliationAttempts >= MAX_RECONCILIATION_ATTEMPTS) {
      reportReconciliationTimeout()
      return
    }

    const timer = setTimeout(async () => {
      await refetch()
      setReconciliationAttempts((n) => n + 1)
    }, RECONCILIATION_POLL_MS)

    return () => clearTimeout(timer)
  }, [
    reconciliationAttempts,
    invoice?.status,
    invoice?.source,
    invoice?.petIds,
    refetch,
    navigate,
    queryClient,
  ])

  function handlePaymentSuccess() {
    setReconciliationAttempts(1)
  }

  if (invoiceLoading || me.isPending) {
    return (
      <PageContainer backLink={BACK_LINK} headline={headline} description={PAGE_DESCRIPTION}>
        <LoadingSpinner />
      </PageContainer>
    )
  }

  if (!invoice) {
    return (
      <NotFound
        headline={['Invoice', 'not found']}
        message="This invoice could not be found. It may have already been paid."
        documentTitle="Invoice not found"
        backLink={{ label: 'Back to billing', to: routes.account.billing.index }}
      />
    )
  }

  const isReconciling =
    reconciliationAttempts > 0 && reconciliationAttempts < MAX_RECONCILIATION_ATTEMPTS

  if (invoice.status !== 'paid' && reconciliationAttempts >= MAX_RECONCILIATION_ATTEMPTS) {
    return (
      <PageContainer backLink={BACK_LINK} headline={headline} description={PAGE_DESCRIPTION}>
        <PaymentReconciliationFailed message="We had some trouble processing your payment" />
      </PageContainer>
    )
  }

  if (isReconciling) {
    return (
      <PageContainer backLink={BACK_LINK} headline={headline} description={PAGE_DESCRIPTION}>
        <PaymentProcessing />
      </PageContainer>
    )
  }

  return (
    <PageContainer backLink={BACK_LINK} headline={headline} description={PAGE_DESCRIPTION}>
      <div className="flex flex-col gap-6">
        {isPendingUpgrade && (
          <Alert variant="warning">
            <Info />
            <AlertTitle>Plan upgrade pending</AlertTitle>
            <AlertDescription>
              Complete payment to finish upgrading your membership plan.
            </AlertDescription>
          </Alert>
        )}

        <InvoiceCard
          invoice={invoice}
          petName={petName}
          action={
            invoice.hostedInvoiceUrl ? (
              <InvoiceStripeLink url={invoice.hostedInvoiceUrl} />
            ) : undefined
          }
        />

        {needsPayment && (
          <StripePaymentWidget
            clientSecret={invoice.clientSecret}
            hostedInvoiceUrl={invoice.hostedInvoiceUrl}
            label={`Pay ${formatCurrency(invoice.amountDue, invoice.currency)}`}
            region={region.id}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    </PageContainer>
  )
}
